import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import Status from "components/Status/Status";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import moment from "moment";
import { ProductionDateRange } from "Production/components/ProductionDateRange";
import { ProductionFormState } from "Production/Detail/ProductionDetailSection";
import React, { useState } from "react";
import { ArtistSidePanel } from "../Company/CompanySidePanel";
import { ProductionSidePanelShowList } from "../SidePanelShowList";
import { sidePanelFreeFieldSections } from "../SidePanelFreeFieldSection";

interface ProductionSidePanelDetailsProps {
  production: server.dto.Production;
  freeFieldCategories: server.dto.FreeFieldCategorySmall[];
}

export const ProductionSidePanelDetails = (props: ProductionSidePanelDetailsProps) => {
  const hasMultipleStatuses = site.currentEnvironment.statuses.production.length > 1;
  const production = props.production;
  const [formState, setFormState] = useState<ProductionFormState>({
    name: production.name,
    status: production.status
  });

  React.useEffect(() => {
    if (props.production) {
      setFormState({
        name: props.production.name,
        status: props.production.status
      });
    }
  }, [props.production]);

  const isInternal = site.currentEnvironment.type === "internal";
  const startMoment = production.startDate ? moment.utc(production.startDate) : null;
  const endMoment = production.endDate ? moment.utc(production.endDate) : null;

  const onValueChanged = (key: string, value: any) => {
    if (key === "name") {
      setFormState(prevState => ({
        ...prevState,
        name: value
      }));
    } else if (key === "status") {
      setFormState(prevState => ({
        ...prevState,
        status: value
      }));
    }
  }

  const getUpdateProductionInstruction = (state: ProductionFormState): server.dto.UpdateProductionDetailsInstruction => ({
    productionId: production.productionId,
    name: state.name,
    statusId: state.status.statusId,
  });

  return <SidePanelDetailSectionTemplate
    isInPageEditable={production.permissions.canModify}
    onValueChanged={onValueChanged}
    fields={[{
      key: "name",
      label: RESX.GeneralLabels.Name,
      render: () => <div>{formState.name}</div>,
      inPageEdit: {
        type: "confirmOrCancelButton",
        initialValue: formState.name,
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => (
          <RequiredTextInput
            validatorKey="name"
            focusOnMount
            maxLength={serverReference.constants.production.maxNameLength}
            value={value}
            onChange={(name) => { onChange(name); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />
        ),
        submitChange: (name: string) => window.CoreApi.Production.UpdateDetails(
          getUpdateProductionInstruction({ ...formState, name }))
      }
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      render: () => <Status status={formState.status} />,
      hidden: !hasMultipleStatuses,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <StatusDropdown
            value={value}
            onChange={onChange}
            mainArea="production"
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "custom",
        initialValue: formState.status,
        fullWidth: true,
        submitChange: (status: server.dto.StatusSmall) => window.CoreApi.Production.UpdateDetails(
          getUpdateProductionInstruction({ ...formState, status }))
      }
    }, {
      key: "date",
      label: RESX.DateTime.lblDate,
      render: () => <ProductionDateRange startDate={startMoment} endDate={endMoment} />,
    }, {
      key: "artists",
      label: site.currentEnvironment.functionGroups.artist.plural,
      render: () => <>
        {production.artists.map(artist =>
          <div key={artist.artistId}>
            <ArtistSidePanel
              companyId={artist.artistId}
              trigger={<a>{artist.name}</a>}
            />
          </div>
        )}
      </>
    },
    ...sidePanelFreeFieldSections(props.freeFieldCategories, production.freeFields)]}
    additionalContent={<>
      <ProductionSidePanelShowList isInternal={isInternal} productionId={production.productionId} />
    </>}
  />
}
